import { Product } from '../../core';
import { formatName } from '../../core/user';
import {
  CustomFields,
  Expense,
  ListTemplate,
  LotTransferItem,
  Membership,
  Note,
  Organization,
  Shipment,
} from '../../types';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { documentNotes } from '../components/documentNotes.template';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { ShipmentComponent, ShipmentTemplateType } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyTableMetadata, formatDecimal, getPrimaryBusiness } from './formatters';

export type InventoryTransferInputType = {
  slug: string;
  organization: Organization;
  transferDate: string;
  contact?: Membership;
  status: string;
  shipment: Shipment;
  lotTransferItems: LotTransferItem[];
  notes: Note[];
  customFields: CustomFields;
  expenses: Expense[];
};

export type InventoryTransferTemplateDataType = Partial<{
  transferNumber: string;
  organization: OrganizationTemplateType;
  businessAddress: AddressTemplateType;
  transferDate: string;
  contact: string;
  status: string;
  shipment: ShipmentTemplateType;
  transferredItems: ListTemplate<{
    description: string;
    lot: string;
    unitsShipped: string;
    weightShipped: string;
    palletTags: string;
  }>;
  totalUnitsShipped: string;
  totalWeightShipped: string;
  notes: ListTemplate<{ text: string }>;
  customFields: CustomFields;
  phoneNumber: string;
  email: string;
}>;

export const InventoryTransferTemplate: DocumentTypePopulator<
  InventoryTransferInputType,
  { productDescriptionTemplate?: string },
  InventoryTransferTemplateDataType
> = {
  populate: (inventoryTransfer, { productDescriptionTemplate }, globalOptions) => {
    if (!inventoryTransfer) {
      return {};
    }

    const { slug, organization, contact, status, shipment, lotTransferItems } = inventoryTransfer;

    const primaryBusiness = getPrimaryBusiness(organization);

    const totalUnitsShipped = lotTransferItems.reduce((acc, item) => acc + item.unitsTransferred, 0);

    const totalWeightShipped = lotTransferItems.reduce((acc, item) => {
      return (
        acc +
        Product.Weight.of(item.lot.product)
          .withLot(item.lot)
          .withLineItemPalletTags(item.palletTagsOnTransferItem)
          .forQuantity(item.unitsTransferred)
      );
    }, 0);

    return {
      transferNumber: slug,
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),
      businessAddress: AddressComponent.templatize(primaryBusiness),
      phoneNumber: primaryBusiness?.phone,
      email: globalOptions.overrideEmail ?? contact?.user?.email,
      contact: formatName(contact?.user, ''),
      status,
      shipment: ShipmentComponent.templatize(shipment),
      totalUnitsShipped: formatDecimal(totalUnitsShipped),
      transferredItems: applyTableMetadata(
        lotTransferItems.map((item) => ({
          description: Product.productDescription(
            item.lot.product,
            productDescriptionTemplate ?? organization.productDescriptionTemplate
          ),
          lot: item.lot.slug,
          unitsShipped: formatDecimal(item.unitsTransferred),
          weightShipped: formatDecimal(
            Product.Weight.of(item.lot.product)
              .withLot(item.lot)
              .withLineItemPalletTags(item.palletTagsOnTransferItem)
              .forQuantity(item.unitsTransferred)
          ),
          palletTags: item.palletTagsOnTransferItem.map((pt) => pt.palletTag.tagNumber).join(', '),
        }))
      ),
      totalWeightShipped: formatDecimal(totalWeightShipped),
      notes: documentNotes(inventoryTransfer.notes, globalOptions.templateId, globalOptions.type),
    };
  },
  definitionMap: {
    transferNumber: { label: 'Transfer Number', type: 'string' },
    businessAddress: { label: 'Business Address', type: 'address', children: AddressComponent.definitionMap },
    phoneNumber: { label: 'Phone Number', type: 'string' },
    email: { label: 'Email', type: 'string' },
    organization: { label: 'Organization', type: 'object', children: OrganizationComponent.definitionMap },
    transferDate: { label: 'Transfer Date', type: 'string' },
    contact: { label: 'Contact', type: 'string' },
    status: { label: 'Status', type: 'string' },
    shipment: {
      label: 'Shipment',
      type: 'object',
      children: ShipmentComponent.definitionMap({ customFieldsRoot: 'inventoryTransfer' }),
    },
    transferredItems: {
      label: 'Transferred Items',
      type: 'list',
      children: {
        description: { label: 'Product', type: 'string' },
        lot: { label: 'Lot Code', type: 'string' },
        weightShipped: { label: 'Weight Shipped', type: 'string' },
        unitsShipped: { label: 'Units Shipped', type: 'string' },
        palletTags: { label: 'Pallet Tags', type: 'string' },
      },
    },
    totalUnitsShipped: { label: 'Total Units Shipped', type: 'string' },
    totalWeightShipped: { label: 'Total Weight Shipped', type: 'string' },
    notes: { label: 'Notes', type: 'list', children: { text: { label: 'Text', type: 'string' } } },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'inventoryTransfer' },
  },
};
